import { useLogger } from "@btcdirect/shared/src/composables/util/logger";
import { useClientInfoStore, useUserInfoStore } from "@btcdirect/stores";
import * as Sentry from "@sentry/vue";
import { App } from "vue";
import { InstanceTypes } from "../types";

const { debug } = useLogger();

export default function initializeSentry(app: App, instanceType: InstanceTypes): void {
    const dsn = import.meta.env.VITE_APPLICATION_LOGGING_DSN;
    if (!dsn) {
        debug("Sentry logging DSN not found", "Assuming this is a development environment");
        return;
    }

    const ignoredErrors = ["Network Error"];

    const environment = import.meta.env.VITE_ENV;

    Sentry.init({
        release: `embeddables@${import.meta.env.VITE_WIDGET_VERSION}`,

        // Vue app instance
        app,
        ignoreErrors: ignoredErrors,
        // Set the environment to log the errors to
        environment: environment,

        // The DSN tells the SDK where to send the events
        dsn,

        // Data to be set to the initial scope.
        // Initial scope can be defined either as an object or a callback function, as shown below.
        initialScope: (scope) => {
            scope.setTag("btcdirect.widget_name", instanceType.toString());
            return scope;
        },

        // integrations: [
        //     new BrowserTracing({
        //         routingInstrumentation: Sentry.vueRouterInstrumentation(router),
        //         tracingOrigins: ["localhost", "my-site-url.com", /^\//],
        //     }),
        // ],.

        // Modify events before sending to Sentry
        beforeSend: (event) => {
            const clientInfoStore = useClientInfoStore();
            const userInfoStore = useUserInfoStore();

            // Add the API client info
            Sentry.setTag("btcdirect.client", clientInfoStore.client?.slug ?? "unknown");

            // Add the user's email address
            Sentry.setTag("btcdirect.user", userInfoStore.user?.emailAddress ?? "unknown");

            return event;
        },
        replaysOnErrorSampleRate: 0.5,
        replaysSessionSampleRate: 0.1,
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 1.0,

        integrations: [
            Sentry.replayIntegration({
                // Additional SDK configuration goes in here, for example:
                maskAllText: true,
                blockAllMedia: true,

            })
        ],
        // If you enable the SDK, Vue will not call its logError internally.
        // As a result, errors occurring in the Vue renderer will not display in the developer console.
        // To preserve this functionality, pass the logErrors: true option.
        logErrors: environment !== "production"
    });
}
